import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Container, Typography, TextField, Button, Paper, Alert, InputAdornment, IconButton } from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset link.');
    }
  }, [token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setValidationError('');
  };

  const validatePassword = () => {
    if (formData.password.length < 8) {
      setValidationError('Password must be at least 8 characters long');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setValidationError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePassword()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // TODO: Replace with actual API call
      // const response = await fetch('/api/auth/reset-password', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ 
      //     token,
      //     newPassword: formData.password 
      //   })
      // });

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Mock success
      setSuccess(true);
      console.log('Password reset successful');

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError('Failed to reset password. The link may have expired. Please request a new one.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: 'calc(100vh - 64px - 300px)', py: { xs: 4, sm: 6, md: 8 } }}>
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: { xs: 3, sm: 4, md: 5 }, borderRadius: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            {success ? (
              <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            ) : (
              <LockResetIcon sx={{ fontSize: 60, color: 'black', mb: 2 }} />
            )}
            <Typography variant="h4" gutterBottom fontWeight={700} sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
              {success ? 'Password Reset!' : 'Reset Password'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {success 
                ? 'Your password has been reset successfully' 
                : 'Enter your new password below'}
            </Typography>
          </Box>

          {success ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body1">
                  Your password has been changed successfully. You can now login with your new password.
                </Typography>
              </Alert>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Redirecting to login page...
              </Typography>

              <Button
                component={Link}
                to="/login"
                variant="contained"
                fullWidth
                size="large"
              >
                Go to Login
              </Button>
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                  {error.includes('expired') && (
                    <Box sx={{ mt: 1 }}>
                      <Button
                        component={Link}
                        to="/forgot-password"
                        variant="text"
                        size="small"
                        sx={{ color: 'error.dark', textTransform: 'none', p: 0 }}
                      >
                        Request new reset link
                      </Button>
                    </Box>
                  )}
                </Alert>
              )}

              {validationError && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  {validationError}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  fullWidth
                  label="New Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={loading || !token}
                  autoFocus
                  helperText="Must be at least 8 characters"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Confirm New Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  disabled={loading || !token}
                  error={formData.confirmPassword !== '' && formData.password !== formData.confirmPassword}
                  helperText={
                    formData.confirmPassword !== '' && formData.password !== formData.confirmPassword
                      ? 'Passwords do not match'
                      : ''
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button 
                  type="submit" 
                  variant="contained" 
                  size="large" 
                  fullWidth
                  disabled={loading || !token || !formData.password || !formData.confirmPassword}
                  sx={{ mt: 2, py: 1.5 }}
                >
                  {loading ? 'Resetting Password...' : 'Reset Password'}
                </Button>
              </Box>
            </>
          )}
        </Paper>

        <Typography variant="body2" align="center" sx={{ mt: 3, color: 'text.secondary' }}>
          Remember your password?{' '}
          <Link to="/login" style={{ color: 'black', fontWeight: 600, textDecoration: 'none' }}>
            Sign in
          </Link>
        </Typography>
      </Container>
    </Box>
  );
};

export default ResetPassword;

